import React from "react";
import { Alert } from "reactstrap";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Input,
    Button,
    ListGroup,
    ListGroupItem,
    Table
} from "reactstrap";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { sessionEnded } from "../user/SessionEnded";
import Api from "../../components/helpers/Api";
import { FiCopy } from "react-icons/fi";
import { RiDeleteBinLine } from "react-icons/ri";
import SeoNoIndex from "../../components/helpers/SeoNoIndex";

class AdminThemeDetail extends React.Component {
    state = {
        isLoading: true,
        themeTitle: "",
        associatedSongs: [],
        searchQuery: "",
        searchResults: [],
        saveSuccess: false,
        saveError: null,
        saving: false,
        unauthorized: false,
        showRemoveAllConfirm: false,
        removingAll: false,
        showDeleteConfirm: false,
        deleting: false,
        allThemes: null,
        mergeSearchQuery: "",
        mergeTarget: null,
        mergeConfirming: false,
        merging: false,
        mergeError: null,
        themeReferences: [],
        removedThemeRefIds: [],
        allBooks: [],
        savingRefs: false,
        saveRefsSuccess: false,
        saveRefsError: null,
        copyRefSearchQuery: "",
        copyRefTarget: null,
        copyRefConfirming: false,
        copyingRefs: false,
        copyRefError: null,
        copyRefSuccess: null
    };

    componentDidMount() {
        try {
            const customer = sessionStorage.getItem("customer");
            const parsed = customer ? JSON.parse(customer) : null;
            console.log("AdminThemeDetail customer structure:", parsed);
            if (!parsed?.user?.isAdmin) {
                this.setState({ unauthorized: true });
                return;
            }
            const { themeId } = this.props.params;
            this.loadTheme(themeId);
            this.fetchAllBooks();
            this.fetchThemeReferences(themeId);
        } catch (e) {
            console.error("AdminThemeDetail componentDidMount error:", e);
            this.setState({ unauthorized: true });
        }
    }

    handleCopyTitle = () => {
        navigator.clipboard.writeText(this.state.themeTitle);
    };

    loadTheme(id) {
        Api.get(`/themes/${id}/with-words`)
            .then(res => {
                this.setState({
                    themeTitle: res.data.title,
                    associatedSongs: Array.isArray(res.data.words)
                        ? res.data.words
                        : [],
                    isLoading: false
                });
            })
            .catch(err => {
                console.error("Error loading theme:", err);
                this.setState({ isLoading: false });
            });
    }

    fetchAllBooks = () => {
        Api.get("/scripture")
            .then((response) => {
                this.setState({ allBooks: response.data });
            })
            .catch((error) => {
                console.error("Error fetching testament books:", error);
            });
    };

    fetchThemeReferences = (id) => {
        Api.get(`/themes/${id}/references`)
            .then((response) => {
                const refs = response.data.map(r => ({
                    id: r.id,
                    bookId: r.bookId,
                    chapter: r.chapter,
                    verseFrom: r.verseFrom,
                    verseTo: r.verseTo
                }));
                this.setState({ themeReferences: refs });
            })
            .catch((error) => {
                console.error("Error fetching theme references:", error);
            });
    };

    handleRemove = (wordId) => {
        const { themeId } = this.props.params;
        Api.delete(`/words/${wordId}/themes/${themeId}`)
            .then(() =>
                this.setState(state => ({
                    associatedSongs: state.associatedSongs.filter(w => w.id !== wordId)
                }))
            )
            .catch(console.error);
    };

    handleSearch = (e) => {
        e.preventDefault();
        const q = this.state.searchQuery.trim();
        if (!q) return;

        Api.get(`/words/search?q=${encodeURIComponent(q)}`)
            .then(res => this.setState({ searchResults: res.data }))
            .catch(console.error);
    };

    handleAdd = (word) => {
        const { themeId } = this.props.params;
        Api.post(`/words/${word.id}/themes/${themeId}`)
            .then(() =>
                this.setState(state => ({
                    associatedSongs: [...state.associatedSongs, word]
                }))
            )
            .catch(console.error);
    };

    handleSaveTitle = () => {
        const { themeId } = this.props.params;

        this.setState({
            saving: true,
            saveError: null,
            saveSuccess: false
        });

        Api.put(`/themes/${themeId}`, {
            id: themeId,
            title: this.state.themeTitle
        })
            .then(res => {
                this.setState({
                    themeTitle: res.data.title,
                    saveSuccess: true
                });

                setTimeout(() => {
                    this.setState({ saveSuccess: false });
                }, 3000);
            })
            .catch(err => {
                if (err.response?.data?.error) {
                    this.setState({
                        saveError: err.response.data.error
                    });
                } else {
                    console.error("Failed to update theme", err);
                }
            })
            .finally(() => {
                this.setState({ saving: false });
            });
    };

    handleRemoveAllSongs = async () => {
        const { themeId } = this.props.params;
        this.setState({ removingAll: true, saveError: null });
        try {
            await Promise.all(
                this.state.associatedSongs.map(song =>
                    Api.delete(`/words/${song.id}/themes/${themeId}`)
                )
            );
            this.setState({ showRemoveAllConfirm: false, removingAll: false });
            this.loadTheme(themeId);
        } catch (err) {
            console.error("Failed to remove all songs", err);
            this.setState({ saveError: "Failed to remove all songs. Please try again.", removingAll: false });
            this.loadTheme(themeId);
        }
    };

    handleDeleteTheme = () => {
        const { themeId } = this.props.params;
        this.setState({ deleting: true, saveError: null });
        Api.delete(`/themes/${themeId}`)
            .then(() => {
                this.props.navigate("/admin/themes");
            })
            .catch(err => {
                const status = err.response?.status;
                const message = status === 409
                    ? "Cannot delete a theme that still has songs."
                    : "Failed to delete theme. Please try again.";
                this.setState({ saveError: message, deleting: false, showDeleteConfirm: false });
            });
    };

    handleLoadMergeThemes = () => {
        if (this.state.allThemes !== null) return;
        Api.get("/themes")
            .then(({ data }) => {
                const sorted = (data || []).sort((a, b) =>
                    a.title.localeCompare(b.title)
                );
                this.setState({ allThemes: sorted });
            })
            .catch(console.error);
    };

    handleMergeSearchChange = (e) => {
        this.setState({ mergeSearchQuery: e.target.value, mergeTarget: null, mergeConfirming: false });
    };

    handleSelectMergeTarget = (theme) => {
        this.setState({ mergeTarget: theme, mergeConfirming: true, mergeSearchQuery: "" });
    };

    handleCancelMerge = () => {
        this.setState({ mergeTarget: null, mergeConfirming: false, mergeError: null });
    };

    handleConfirmMerge = () => {
        const { themeId } = this.props.params;
        const { mergeTarget } = this.state;

        this.setState({ merging: true, mergeError: null });

        Api.post(`/themes/${themeId}/merge-into/${mergeTarget.id}`)
            .then(() => {
                this.props.navigate(`/admin/theme/${mergeTarget.id}`);
            })
            .catch(err => {
                console.error("Merge failed", err);
                this.setState({
                    mergeError: err.response?.data?.error || "Merge failed. Please try again.",
                    merging: false
                });
            });
    };

    handleAddThemeReference = () => {
        this.setState(prev => ({
            themeReferences: [
                ...prev.themeReferences,
                { id: null, bookId: "", chapter: "", verseFrom: "", verseTo: "" }
            ]
        }));
    };

    handleRemoveThemeReference = (index) => {
        this.setState(prev => {
            const refs = [...prev.themeReferences];
            const removedThemeRefIds = [...prev.removedThemeRefIds];
            const ref = refs[index];
            if (ref.id) {
                removedThemeRefIds.push(ref.id);
            }
            refs.splice(index, 1);
            return { themeReferences: refs, removedThemeRefIds };
        });
    };

    handleThemeReferenceChange = (index, field, value) => {
        this.setState(prev => {
            const refs = [...prev.themeReferences];
            const ref = { ...refs[index] };
            ref[field] = value;
            if (field === "bookId") {
                ref.chapter = "";
                ref.verseFrom = "";
                ref.verseTo = "";
            }
            if (field === "chapter") {
                ref.verseFrom = "";
                ref.verseTo = "";
            }
            if (field === "verseFrom") {
                ref.verseTo = "";
            }
            refs[index] = ref;
            return { themeReferences: refs };
        });
    };

    handleSaveThemeReferences = async () => {
        const { themeId } = this.props.params;
        const { themeReferences, removedThemeRefIds } = this.state;
        const toReferencePayload = (ref) => ({
            bookId: ref.bookId,
            chapter: Number(ref.chapter),
            verseFrom: ref.verseFrom ? Number(ref.verseFrom) : null,
            verseTo: ref.verseTo ? Number(ref.verseTo) : null
        });

        this.setState({ savingRefs: true, saveRefsError: null, saveRefsSuccess: false });

        try {
            if (removedThemeRefIds && removedThemeRefIds.length) {
                for (const rid of removedThemeRefIds) {
                    await Api.delete(`/themes/${themeId}/references/${rid}`);
                }
            }

            for (const ref of themeReferences) {
                if (ref.id) {
                    await Api.put(`/themes/${themeId}/references/${ref.id}`, toReferencePayload(ref));
                }
            }

            for (const ref of themeReferences) {
                if (!ref.id) {
                    await Api.post(`/themes/${themeId}/references`, toReferencePayload(ref));
                }
            }

            this.setState({ savingRefs: false, saveRefsSuccess: true, removedThemeRefIds: [] });
            this.fetchThemeReferences(themeId);
            setTimeout(() => this.setState({ saveRefsSuccess: false }), 3000);
        } catch (err) {
            console.error("Failed to save theme references", err);
            this.setState({
                savingRefs: false,
                saveRefsError: err.response?.data?.error || "Failed to save references. Please try again."
            });
        }
    };

    handleCopyRefSearchChange = (e) => {
        this.setState({ copyRefSearchQuery: e.target.value, copyRefTarget: null, copyRefConfirming: false });
    };

    handleSelectCopyRefTarget = (theme) => {
        this.setState({ copyRefTarget: theme, copyRefConfirming: true, copyRefSearchQuery: "" });
    };

    handleCancelCopyRef = () => {
        this.setState({ copyRefTarget: null, copyRefConfirming: false, copyRefError: null });
    };

    handleConfirmCopyRef = () => {
        const { themeId } = this.props.params;
        const { copyRefTarget } = this.state;

        this.setState({ copyingRefs: true, copyRefError: null });

        Api.post(`/themes/${themeId}/copy-references-to/${copyRefTarget.id}`)
            .then(res => {
                const added = res.data?.added || 0;
                const message = added === 0
                    ? "0 references copied — all already present"
                    : `${added} reference${added !== 1 ? "s" : ""} copied to ${copyRefTarget.title}.`;
                this.setState({
                    copyRefSuccess: message,
                    copyRefConfirming: false,
                    copyRefTarget: null,
                    copyRefError: null,
                    copyingRefs: false
                });
                setTimeout(() => this.setState({ copyRefSuccess: null }), 3000);
            })
            .catch(err => {
                console.error("Copy references failed", err);
                this.setState({
                    copyRefError: err.response?.data?.error || "Copy failed. Please try again.",
                    copyingRefs: false
                });
            });
    };

    render() {
        if (!sessionStorage.getItem("customer")) return sessionEnded();
        if (this.state.unauthorized) return <Navigate to="/" />;

        const {
            isLoading,
            themeTitle,
            associatedSongs,
            searchQuery,
            searchResults,
            saveSuccess,
            saveError,
            saving,
            showRemoveAllConfirm,
            removingAll,
            showDeleteConfirm,
            deleting,
            allThemes,
            mergeSearchQuery,
            mergeTarget,
            mergeConfirming,
            merging,
            mergeError,
            themeReferences,
            allBooks,
            savingRefs,
            saveRefsSuccess,
            saveRefsError,
            copyRefSearchQuery,
            copyRefTarget,
            copyRefConfirming,
            copyingRefs,
            copyRefError,
            copyRefSuccess
        } = this.state;

        const { themeId } = this.props.params;
        const currentThemeId = String(themeId);

        const mergeSearchResults = allThemes && mergeSearchQuery.trim().length >= 2
            ? allThemes.filter(t =>
                String(t.id) !== currentThemeId &&
                t.title.toLowerCase().includes(mergeSearchQuery.toLowerCase())
              ).slice(0, 10)
            : [];

        const copyRefSearchResults = allThemes && copyRefSearchQuery.trim().length >= 2
            ? allThemes.filter(t =>
                String(t.id) !== currentThemeId &&
                t.title.toLowerCase().includes(copyRefSearchQuery.toLowerCase())
              ).slice(0, 10)
            : [];

        return (
            <>
                <SeoNoIndex noindex={true} />

                <section className="section section-lg section-shaped pb-10">
                    <div className="shape shape-style-1 shape-default">
                        <span /><span /><span /><span /><span /><span /><span /><span /><span />
                    </div>
                    <Container className="py-lg-md d-flex">
                        <Row className="align-items-center">
                            <Col lg="12">
                                <h1 className="display-3 text-white d-flex align-items-center">
                                    Edit Theme:&nbsp;
                                    <span>{themeTitle}</span>
                                    &nbsp;
                                    <Button
                                        color="link"
                                        onClick={this.handleCopyTitle}
                                        className="p-0 text-white"
                                    >
                                        <FiCopy size={20} />
                                    </Button>
                                </h1>
                            </Col>
                        </Row>
                    </Container>
                </section>

                <Container fluid className="pb-5">
                    <Row className="justify-content-center">
                        <Col xl="10" lg="12">
                            <Card className="shadow border-0">
                                <CardBody>

                                    <Button
                                        color="link"
                                        onClick={() => this.props.navigate("/admin/themes")}
                                        className="mb-3 p-0"
                                    >
                                        &larr; Back to list
                                    </Button>

                                    <Container className="mt-4">

                                        {saveSuccess && (
                                            <Alert color="success">
                                                Theme updated successfully
                                            </Alert>
                                        )}

                                        {saveError && (
                                            <Alert color="danger">
                                                {saveError}
                                            </Alert>
                                        )}

                                        <Row className="mb-4">
                                            <Col>
                                                <Input
                                                    type="text"
                                                    value={themeTitle}
                                                    onChange={(e) =>
                                                        this.setState({ themeTitle: e.target.value })
                                                    }
                                                />
                                            </Col>
                                            <Col className="text-right">
                                                <Button
                                                    color="primary"
                                                    onClick={this.handleSaveTitle}
                                                    disabled={saving}
                                                >
                                                    {saving ? "Saving..." : "Save"}
                                                </Button>
                                                &nbsp;
                                                <Button
                                                    color="secondary"
                                                    onClick={() => this.props.navigate("/admin/themes")}
                                                >
                                                    Back
                                                </Button>
                                            </Col>
                                        </Row>

                                        {isLoading ? (
                                            <div>Loading…</div>
                                        ) : (
                                            <>
                                                <h5>Associated Songs</h5>

                                                <ListGroup className="mb-4">
                                                    {associatedSongs.length > 0 ? (
                                                        associatedSongs.map(w => (
                                                            <ListGroupItem
                                                                key={w.id}
                                                                className="d-flex justify-content-between align-items-center"
                                                            >
                                                                <span
                                                                    style={{ cursor: "pointer" }}
                                                                    onClick={() =>
                                                                        this.props.navigate(`/admin/song/${w.id}`)
                                                                    }
                                                                >
                                                                    <strong>{w.title}</strong>
                                                                    {w.alternativeTitle && (
                                                                        <> <em>({w.alternativeTitle})</em></>
                                                                    )}
                                                                </span>

                                                                <Button
                                                                    size="sm"
                                                                    color="danger"
                                                                    onClick={() => this.handleRemove(w.id)}
                                                                >
                                                                    Remove
                                                                </Button>
                                                            </ListGroupItem>
                                                        ))
                                                    ) : (
                                                        <ListGroupItem>No songs linked.</ListGroupItem>
                                                    )}
                                                </ListGroup>

                                                {associatedSongs.length > 0 && (
                                                    <div className="mb-4">
                                                        {!showRemoveAllConfirm ? (
                                                            <Button
                                                                color="warning"
                                                                size="sm"
                                                                onClick={() => this.setState({ showRemoveAllConfirm: true, showDeleteConfirm: false })}
                                                            >
                                                                Remove all songs
                                                            </Button>
                                                        ) : (
                                                            <Alert color="warning">
                                                                <strong>Remove all {associatedSongs.length} songs from this theme?</strong> This cannot be undone.
                                                                <div className="mt-2">
                                                                    <Button
                                                                        color="danger"
                                                                        size="sm"
                                                                        disabled={removingAll}
                                                                        onClick={this.handleRemoveAllSongs}
                                                                    >
                                                                        {removingAll ? "Removing..." : "Confirm remove all"}
                                                                    </Button>
                                                                    {" "}
                                                                    <Button
                                                                        color="secondary"
                                                                        size="sm"
                                                                        disabled={removingAll}
                                                                        onClick={() => this.setState({ showRemoveAllConfirm: false })}
                                                                    >
                                                                        Cancel
                                                                    </Button>
                                                                </div>
                                                            </Alert>
                                                        )}
                                                    </div>
                                                )}

                                                <h5>Add Songs to Theme</h5>

                                                <form onSubmit={this.handleSearch} className="mb-3 d-flex">
                                                    <Input
                                                        type="text"
                                                        placeholder="Search songs by title…"
                                                        value={searchQuery}
                                                        onChange={(e) =>
                                                            this.setState({ searchQuery: e.target.value })
                                                        }
                                                    />
                                                    <Button color="primary" type="submit" className="ml-2">
                                                        Search
                                                    </Button>
                                                </form>

                                                <ListGroup>
                                                    {searchResults.map(w => {
                                                        const already = associatedSongs.some(a => a.id === w.id);

                                                        return (
                                                            <ListGroupItem
                                                                key={w.id}
                                                                className="d-flex justify-content-between align-items-center"
                                                            >
                                                                <span
                                                                    style={{ cursor: "pointer" }}
                                                                    onClick={() =>
                                                                        this.props.navigate(`/admin/song/${w.id}`)
                                                                    }
                                                                >
                                                                    {w.title}
                                                                    {w.alternativeTitle && (
                                                                        <> <em>({w.alternativeTitle})</em></>
                                                                    )}
                                                                </span>

                                                                {!already && (
                                                                    <Button
                                                                        size="sm"
                                                                        color="success"
                                                                        onClick={() => this.handleAdd(w)}
                                                                    >
                                                                        Add
                                                                    </Button>
                                                                )}
                                                            </ListGroupItem>
                                                        );
                                                    })}

                                                    {searchResults.length === 0 && searchQuery && (
                                                        <ListGroupItem>No matching songs.</ListGroupItem>
                                                    )}
                                                </ListGroup>

                                                <hr />

                                                <h5 className="mt-4">Scripture References</h5>

                                                {saveRefsSuccess && (
                                                    <Alert color="success">References saved successfully</Alert>
                                                )}

                                                {saveRefsError && (
                                                    <Alert color="danger">{saveRefsError}</Alert>
                                                )}

                                                <Table responsive bordered size="sm" className="mb-2">
                                                    <thead className="thead-light">
                                                    <tr>
                                                        <th>Book</th>
                                                        <th style={{ width: "10%" }}>Chapter</th>
                                                        <th style={{ width: "10%" }}>Verse From</th>
                                                        <th style={{ width: "10%" }}>Verse To</th>
                                                        <th style={{ width: "5%" }}></th>
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    {themeReferences.length > 0 ? themeReferences.map((ref, idx) => {
                                                        const bookInfo = allBooks.find(b => b.id === ref.bookId);
                                                        const chapterCount = bookInfo ? bookInfo.chapterCount : 0;
                                                        const versesCountMap = bookInfo ? bookInfo.versesCount || {} : {};
                                                        const chapterOptions = [];
                                                        for (let c = 1; c <= (chapterCount || 0); c++) chapterOptions.push(c);
                                                        const verseCount = ref.chapter && versesCountMap[ref.chapter] ? versesCountMap[ref.chapter] : 0;
                                                        const verseOptions = [];
                                                        for (let v = 1; v <= verseCount; v++) verseOptions.push(v);

                                                        return (
                                                            <tr key={idx}>
                                                                <td>
                                                                    <Input
                                                                        type="select"
                                                                        value={ref.bookId}
                                                                        onChange={e => this.handleThemeReferenceChange(idx, "bookId", e.target.value)}
                                                                    >
                                                                        <option value="">Select book</option>
                                                                        {allBooks.map(bk => (
                                                                            <option key={bk.id} value={bk.id}>
                                                                                {bk.title}
                                                                            </option>
                                                                        ))}
                                                                    </Input>
                                                                </td>
                                                                <td>
                                                                    <Input
                                                                        type="select"
                                                                        value={ref.chapter || ""}
                                                                        disabled={!ref.bookId}
                                                                        onChange={e => this.handleThemeReferenceChange(idx, "chapter", e.target.value)}
                                                                    >
                                                                        <option value="">-</option>
                                                                        {chapterOptions.map(c => (
                                                                            <option key={c} value={c}>{c}</option>
                                                                        ))}
                                                                    </Input>
                                                                </td>
                                                                <td>
                                                                    <Input
                                                                        type="select"
                                                                        value={ref.verseFrom || ""}
                                                                        disabled={!ref.chapter}
                                                                        onChange={e => this.handleThemeReferenceChange(idx, "verseFrom", e.target.value)}
                                                                    >
                                                                        <option value="">-</option>
                                                                        {verseOptions.map(v => (
                                                                            <option key={v} value={v}>{v}</option>
                                                                        ))}
                                                                    </Input>
                                                                </td>
                                                                <td>
                                                                    <Input
                                                                        type="select"
                                                                        value={ref.verseTo || ""}
                                                                        disabled={!ref.verseFrom}
                                                                        onChange={e => this.handleThemeReferenceChange(idx, "verseTo", e.target.value)}
                                                                    >
                                                                        <option value="">-</option>
                                                                        {verseOptions
                                                                            .filter(v => !ref.verseFrom || v >= Number(ref.verseFrom))
                                                                            .map(v => (
                                                                                <option key={v} value={v}>{v}</option>
                                                                            ))
                                                                        }
                                                                    </Input>
                                                                </td>
                                                                <td className="text-center">
                                                                    <Button
                                                                        color="link"
                                                                        className="p-0 text-danger"
                                                                        onClick={() => this.handleRemoveThemeReference(idx)}
                                                                        title="Remove"
                                                                    >
                                                                        <RiDeleteBinLine size={18}/>
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    }) : (
                                                        <tr>
                                                            <td colSpan="5" className="text-center text-muted">
                                                                No references added.
                                                            </td>
                                                        </tr>
                                                    )}
                                                    </tbody>
                                                </Table>

                                                <div className="d-flex mb-3">
                                                    <Button color="secondary" size="sm" onClick={this.handleAddThemeReference} className="mr-2">
                                                        + Add Reference
                                                    </Button>
                                                    <Button color="primary" size="sm" onClick={this.handleSaveThemeReferences} disabled={savingRefs}>
                                                        {savingRefs ? "Saving..." : "Save References"}
                                                    </Button>
                                                </div>

                                                <hr />

                                                <h5 className="mt-4">Copy Scripture References</h5>

                                                <p className="text-muted small">
                                                    Copy all scripture references from <strong>{themeTitle}</strong> to another theme. The source theme keeps its references. Duplicates are skipped.
                                                </p>

                                                {copyRefSuccess && (
                                                    <Alert color="success">{copyRefSuccess}</Alert>
                                                )}

                                                {copyRefError && (
                                                    <Alert color="danger">{copyRefError}</Alert>
                                                )}

                                                {!copyRefConfirming ? (
                                                    <>
                                                        <Input
                                                            type="text"
                                                            placeholder="Search for target theme (min. 2 characters)…"
                                                            value={copyRefSearchQuery}
                                                            onChange={this.handleCopyRefSearchChange}
                                                            onFocus={this.handleLoadMergeThemes}
                                                            className="mb-2"
                                                        />

                                                        {allThemes === null && copyRefSearchQuery.length > 0 && (
                                                            <p className="text-muted small">Loading themes…</p>
                                                        )}

                                                        {copyRefSearchResults.length > 0 && (
                                                            <ListGroup>
                                                                {copyRefSearchResults.map(t => (
                                                                    <ListGroupItem
                                                                        key={t.id}
                                                                        className="d-flex justify-content-between align-items-center"
                                                                        style={{ cursor: "pointer" }}
                                                                        onClick={() => this.handleSelectCopyRefTarget(t)}
                                                                    >
                                                                        {t.title}
                                                                        <span className="text-muted small ml-2">
                                                                            {t.id}
                                                                        </span>
                                                                    </ListGroupItem>
                                                                ))}
                                                            </ListGroup>
                                                        )}

                                                        {allThemes !== null &&
                                                            copyRefSearchQuery.trim().length >= 2 &&
                                                            copyRefSearchResults.length === 0 && (
                                                            <p className="text-muted small">No matching themes.</p>
                                                        )}
                                                    </>
                                                ) : (
                                                    <Alert color="warning">
                                                        <p className="mb-3">
                                                            <strong>Are you sure?</strong> All{' '}
                                                            <strong>{themeReferences.length} reference{themeReferences.length !== 1 ? 's' : ''}</strong>{' '}
                                                            from <strong>{themeTitle}</strong> will be copied to{' '}
                                                            <strong>{copyRefTarget.title}</strong>. The source theme will keep its references. Duplicates will be skipped.
                                                        </p>
                                                        <Button
                                                            color="danger"
                                                            onClick={this.handleConfirmCopyRef}
                                                            disabled={copyingRefs}
                                                            className="mr-2"
                                                        >
                                                            {copyingRefs ? "Copying…" : `Confirm — copy to ${copyRefTarget.title}`}
                                                        </Button>
                                                        <Button
                                                            color="secondary"
                                                            onClick={this.handleCancelCopyRef}
                                                            disabled={copyingRefs}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </Alert>
                                                )}

                                                <hr />

                                                <h5 className="mt-4 text-danger">Merge Theme</h5>

                                                <p className="text-muted small">
                                                    Move all songs from <strong>{themeTitle}</strong> into another theme. The source theme will not be deleted. This action cannot be undone.
                                                </p>

                                                {mergeError && (
                                                    <Alert color="danger">{mergeError}</Alert>
                                                )}

                                                {!mergeConfirming ? (
                                                    <>
                                                        <Input
                                                            type="text"
                                                            placeholder="Search for target theme (min. 2 characters)…"
                                                            value={mergeSearchQuery}
                                                            onChange={this.handleMergeSearchChange}
                                                            onFocus={this.handleLoadMergeThemes}
                                                            className="mb-2"
                                                        />

                                                        {allThemes === null && mergeSearchQuery.length > 0 && (
                                                            <p className="text-muted small">Loading themes…</p>
                                                        )}

                                                        {mergeSearchResults.length > 0 && (
                                                            <ListGroup>
                                                                {mergeSearchResults.map(t => (
                                                                    <ListGroupItem
                                                                        key={t.id}
                                                                        className="d-flex justify-content-between align-items-center"
                                                                        style={{ cursor: "pointer" }}
                                                                        onClick={() => this.handleSelectMergeTarget(t)}
                                                                    >
                                                                        {t.title}
                                                                        <span className="text-muted small ml-2">
                                                                            {t.id}
                                                                        </span>
                                                                    </ListGroupItem>
                                                                ))}
                                                            </ListGroup>
                                                        )}

                                                        {allThemes !== null &&
                                                            mergeSearchQuery.trim().length >= 2 &&
                                                            mergeSearchResults.length === 0 && (
                                                            <p className="text-muted small">No matching themes.</p>
                                                        )}
                                                    </>
                                                ) : (
                                                    <Alert color="warning">
                                                        <p className="mb-3">
                                                            <strong>Are you sure?</strong> All{' '}
                                                            <strong>{associatedSongs.length} song{associatedSongs.length !== 1 ? 's' : ''}</strong>{' '}
                                                            from <strong>{themeTitle}</strong> will be moved to{' '}
                                                            <strong>{mergeTarget.title}</strong>. The source theme will not be deleted.
                                                        </p>
                                                        <Button
                                                            color="danger"
                                                            onClick={this.handleConfirmMerge}
                                                            disabled={merging}
                                                            className="mr-2"
                                                        >
                                                            {merging ? "Merging…" : `Confirm — merge into ${mergeTarget.title}`}
                                                        </Button>
                                                        <Button
                                                            color="secondary"
                                                            onClick={this.handleCancelMerge}
                                                            disabled={merging}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </Alert>
                                                )}

                                                {associatedSongs.length === 0 && (
                                                    <div className="mt-4 pt-3" style={{ borderTop: "1px solid #dee2e6" }}>
                                                        <h5 className="text-danger">Danger zone</h5>
                                                        {!showDeleteConfirm ? (
                                                            <Button
                                                                color="danger"
                                                                size="sm"
                                                                onClick={() => this.setState({ showDeleteConfirm: true, showRemoveAllConfirm: false })}
                                                            >
                                                                Delete theme
                                                            </Button>
                                                        ) : (
                                                            <Alert color="danger">
                                                                <strong>Permanently delete "{themeTitle}"?</strong> This will also delete any scripture references for this theme. This cannot be undone.
                                                                <div className="mt-2">
                                                                    <Button
                                                                        color="danger"
                                                                        size="sm"
                                                                        disabled={deleting}
                                                                        onClick={this.handleDeleteTheme}
                                                                    >
                                                                        {deleting ? "Deleting..." : "Confirm delete"}
                                                                    </Button>
                                                                    {" "}
                                                                    <Button
                                                                        color="secondary"
                                                                        size="sm"
                                                                        disabled={deleting}
                                                                        onClick={() => this.setState({ showDeleteConfirm: false })}
                                                                    >
                                                                        Cancel
                                                                    </Button>
                                                                </div>
                                                            </Alert>
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </Container>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </>
        );
    }
}

export default withRouter(AdminThemeDetail);

function withRouter(Component) {
    return props => {
        const navigate = useNavigate();
        const params = useParams();
        return <Component {...props} navigate={navigate} params={params} />;
    };
}
